import React from 'react';

interface AvatarProps {
  name?: string;
  src?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

export const Avatar: React.FC<AvatarProps> = ({ 
  name = 'Usuário', 
  src, 
  size = 'md', 
  className = '' 
}) => {
  const getInitials = (fullName: string): string => {
    return fullName
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'w-8 h-8 text-xs';
      case 'md':
        return 'w-10 h-10 text-sm';
      case 'lg':
        return 'w-12 h-12 text-base';
      case 'xl':
        return 'w-16 h-16 text-lg';
      default:
        return 'w-10 h-10 text-sm';
    }
  };

  const getGradientColor = (name: string): string => {
    const colors = [
      'from-blue-500 to-blue-600',
      'from-green-500 to-green-600',
      'from-purple-500 to-purple-600',
      'from-pink-500 to-pink-600',
      'from-indigo-500 to-indigo-600',
      'from-orange-500 to-orange-600',
      'from-teal-500 to-teal-600',
      'from-red-500 to-red-600',
      'from-cyan-500 to-cyan-600',
      'from-yellow-500 to-yellow-600'
    ];
    
    // Simple hash function to consistently assign colors based on name
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
  };

  const sizeClasses = getSizeClasses();
  const gradientColor = getGradientColor(name);

  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className={`${sizeClasses} rounded-full border-2 border-primary-200 object-cover ${className}`}
        onError={(e) => {
          // If image fails to load, hide it and show initials instead
          e.currentTarget.style.display = 'none';
          const fallback = e.currentTarget.nextElementSibling as HTMLElement;
          if (fallback) {
            fallback.style.display = 'flex';
          }
        }}
      />
    );
  }

  return (
    <div className={`${sizeClasses} rounded-full bg-gradient-to-br ${gradientColor} flex items-center justify-center font-bold text-white shadow-lg ${className}`}>
      {getInitials(name)}
    </div>
  );
};

// Versão híbrida que mostra a imagem se disponível, senão mostra as iniciais
export const HybridAvatar: React.FC<AvatarProps> = ({ 
  name = 'Usuário', 
  src, 
  size = 'md', 
  className = '' 
}) => {
  const getInitials = (fullName: string): string => {
    return fullName
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'w-8 h-8 text-xs';
      case 'md':
        return 'w-10 h-10 text-sm';
      case 'lg':
        return 'w-12 h-12 text-base';
      case 'xl':
        return 'w-16 h-16 text-lg';
      default:
        return 'w-10 h-10 text-sm';
    }
  };

  const getGradientColor = (name: string): string => {
    const colors = [
      'from-blue-500 to-blue-600',
      'from-green-500 to-green-600',
      'from-purple-500 to-purple-600',
      'from-pink-500 to-pink-600',
      'from-indigo-500 to-indigo-600',
      'from-orange-500 to-orange-600',
      'from-teal-500 to-teal-600',
      'from-red-500 to-red-600',
      'from-cyan-500 to-cyan-600',
      'from-yellow-500 to-yellow-600'
    ];
    
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
  };

  const sizeClasses = getSizeClasses();
  const gradientColor = getGradientColor(name);

  return (
    <div className={`relative ${sizeClasses} ${className}`}>
      {src && src.trim() !== '' ? (
        <>
          <img
            src={src}
            alt={name}
            className={`${sizeClasses} rounded-full border-2 border-primary-200 object-cover`}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const fallback = e.currentTarget.nextElementSibling as HTMLElement;
              if (fallback) {
                fallback.style.display = 'flex';
              }
            }}
          />
          <div 
            className={`${sizeClasses} rounded-full bg-gradient-to-br ${gradientColor} flex items-center justify-center font-bold text-white shadow-lg absolute inset-0`}
            style={{ display: 'none' }}
          >
            {getInitials(name)}
          </div>
        </>
      ) : (
        <div className={`${sizeClasses} rounded-full bg-gradient-to-br ${gradientColor} flex items-center justify-center font-bold text-white shadow-lg`}>
          {getInitials(name)}
        </div>
      )}
    </div>
  );
};